// src/TenantProvider.jsx
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';

const TenantContext = createContext(null);
export const useTenant = () => useContext(TenantContext);

const STORAGE_KEY = 'activeCompanyId';

export function TenantProvider({ children }) {
  const { getToken, isSignedIn } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [activeCompany, setActiveCompanyState] = useState(null);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [error, setError] = useState(null);

  // Wrapper that persists selection so refreshes restore the same tenant
  const setActiveCompany = useCallback((company) => {
    setActiveCompanyState(company);
    try {
      if (company?.id) {
        sessionStorage.setItem(STORAGE_KEY, String(company.id));
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch (e) {
      console.warn('Could not persist active company:', e);
    }
  }, []);

  useEffect(() => {
    (async () => {
      if (!isSignedIn) {
        // Clear persisted tenant on sign-out so next user starts clean
        try { sessionStorage.removeItem(STORAGE_KEY); } catch (_) {}
        return;
      }
      setLoadingCompanies(true);
      setError(null);
      try {
        const token = await getToken();
        const res = await fetch('/api/users/me/companies', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error(`Failed to load companies (${res.status})`);
        const data = await res.json();
        const list = data?.companies ?? [];
        setCompanies(list);

        // Restore previously selected tenant if still accessible, else fall back
        let savedId = null;
        try { savedId = sessionStorage.getItem(STORAGE_KEY); } catch (_) {}
        const restored = savedId
          ? list.find((c) => String(c.id) === String(savedId))
          : null;
        const next = restored ?? list[0] ?? null;

        setActiveCompanyState(next);
        try {
          if (next?.id) {
            sessionStorage.setItem(STORAGE_KEY, String(next.id));
          } else {
            sessionStorage.removeItem(STORAGE_KEY);
          }
        } catch (_) {}
      } catch (e) {
        console.error('Tenant load error:', e);
        setError(e.message);
      } finally {
        setLoadingCompanies(false);
      }
    })();
  }, [isSignedIn, getToken]);

  return (
    <TenantContext.Provider
      value={{ companies, activeCompany, setActiveCompany, loadingCompanies, error }}
    >
      {children}
    </TenantContext.Provider>
  );
}